import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import {
  BookOpen,
  Search,
  Sparkles,
  ArrowRight,
  Wrench,
  Clock3,
  Layers3,
  PlayCircle,
} from "lucide-react";
import howtoData from "../data/howto.json";
import { SearchBar } from "../components/SearchBar";

function BluePanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function StatPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
        <span className="text-dtf-accent">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-base font-semibold text-white">{value}</div>
    </div>
  );
}

function MetaChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs font-medium text-dtf-text-muted">
      {children}
    </span>
  );
}

function HowToCard({
  guide,
}: {
  guide: {
    id: string;
    title: string;
    category?: string;
    level?: string;
    timeEstimate?: string;
    summary?: string;
    wizardTip?: string;
    tools?: string[];
    steps?: unknown[];
    symptoms?: string[];
    videoLinks?: unknown[];
  };
}) {
  return (
    <Link
      to={`/howto/${guide.id}`}
      className="group relative block overflow-hidden rounded-[26px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] p-5 transition duration-200 hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]"
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-2">
            {guide.category && (
              <span className="inline-flex rounded-full border border-dtf-accent/20 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-accent">
                {guide.category}
              </span>
            )}

            {guide.level && (
              <span className="inline-flex rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-xs font-medium capitalize text-dtf-blue">
                {guide.level}
              </span>
            )}

            {guide.timeEstimate && (
              <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs font-medium text-dtf-text-muted">
                <Clock3 className="h-3 w-3 text-dtf-accent" />
                {guide.timeEstimate}
              </span>
            )}
          </div>

          <h2 className="mt-4 text-2xl font-bold tracking-tight text-white transition group-hover:text-dtf-blue">
            {guide.title}
          </h2>

          {guide.summary && (
            <p className="mt-3 line-clamp-2 text-sm leading-7 text-dtf-text">
              {guide.summary}
            </p>
          )}

          {guide.wizardTip && (
            <div className="mt-4 rounded-2xl border border-dtf-accent/15 bg-black/20 p-4">
              <div className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-dtf-accent">
                <Sparkles className="h-3.5 w-3.5" />
                Wizard Tip
              </div>
              <p className="line-clamp-2 text-sm leading-7 text-dtf-text-muted">
                {guide.wizardTip}
              </p>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {guide.tools && guide.tools.length > 0 && (
              <MetaChip>
                {guide.tools.length} tool{guide.tools.length !== 1 ? "s" : ""}
              </MetaChip>
            )}

            {guide.steps && guide.steps.length > 0 && (
              <MetaChip>
                {guide.steps.length} step{guide.steps.length !== 1 ? "s" : ""}
              </MetaChip>
            )}

            {guide.symptoms && guide.symptoms.length > 0 && (
              <MetaChip>
                {guide.symptoms.length} symptom
                {guide.symptoms.length !== 1 ? "s" : ""}
              </MetaChip>
            )}

            {guide.videoLinks && guide.videoLinks.length > 0 && (
              <MetaChip>
                <span className="inline-flex items-center gap-1">
                  <PlayCircle className="h-3.5 w-3.5 text-dtf-blue" />
                  Video included
                </span>
              </MetaChip>
            )}
          </div>
        </div>

        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-blue transition group-hover:border-dtf-blue/30 group-hover:bg-dtf-blue/10">
          <ArrowRight className="h-5 w-5" />
        </div>
      </div>
    </Link>
  );
}

export default function HowTo() {
  const [search, setSearch] = useState("");

  const filteredHowtos = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return howtoData.howtos;

    return howtoData.howtos.filter((h) => {
      const titleMatch = h.title?.toLowerCase().includes(q);
      const categoryMatch = h.category?.toLowerCase().includes(q);
      const summaryMatch = h.summary?.toLowerCase().includes(q);
      const wizardTipMatch = h.wizardTip?.toLowerCase().includes(q);
      const symptomsMatch = h.symptoms?.some((symptom) =>
        symptom.toLowerCase().includes(q)
      );
      const toolsMatch = h.tools?.some((tool) => tool.toLowerCase().includes(q));

      return (
        titleMatch ||
        categoryMatch ||
        summaryMatch ||
        wizardTipMatch ||
        symptomsMatch ||
        toolsMatch
      );
    });
  }, [search]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <BluePanel className="mb-6">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
                <BookOpen className="h-3.5 w-3.5" />
                How-To Guides
              </div>

              <h1 className="mb-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                Step-by-step help that actually gets you moving
              </h1>

              <p className="text-lg leading-8 text-dtf-text">
                Find clear walkthroughs for pressing, powder, curing, washing,
                artwork prep, maintenance, and common DTF fixes.
              </p>
            </div>

            <div className="grid gap-3 sm:min-w-[320px] sm:grid-cols-2">
              <StatPill
                icon={<Layers3 className="h-4 w-4" />}
                label="Total Guides"
                value={`${howtoData.howtos.length}`}
              />
              <StatPill
                icon={<Search className="h-4 w-4" />}
                label="Showing"
                value={`${filteredHowtos.length}`}
              />
            </div>
          </div>
        </div>
      </BluePanel>

      <BluePanel className="mb-6">
        <div className="border-b border-white/8 px-6 py-5">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
            <Search className="h-3.5 w-3.5" />
            Search
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-white">
            Search the how-to library
          </h2>

          <p className="mt-2 max-w-3xl text-dtf-text-muted">
            Search by guide title, symptom, tool, summary, category, or wizard tip.
          </p>
        </div>

        <div className="p-6">
          <SearchBar
            value={search}
            onSearch={setSearch}
            placeholder="Search how-tos, symptoms, tools, or category..."
          />
        </div>
      </BluePanel>

      <BluePanel className="mb-6">
        <div className="flex items-center justify-between gap-4 px-6 py-5">
          <div>
            <div className="text-sm text-dtf-text-muted">Showing results</div>
            <div className="text-2xl font-bold text-white">
              {filteredHowtos.length} guide{filteredHowtos.length !== 1 ? "s" : ""}
              {search.trim() ? " found" : ""}
            </div>
          </div>

          <div className="rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-dtf-blue">
            Library visible
          </div>
        </div>
      </BluePanel>

      {filteredHowtos.length > 0 ? (
        <div className="grid gap-5 lg:grid-cols-2">
          {filteredHowtos.map((guide) => (
            <HowToCard key={guide.id} guide={guide} />
          ))}
        </div>
      ) : (
        <BluePanel>
          <div className="px-6 py-14 text-center">
            <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-3xl border border-white/10 bg-black/20">
              <Wrench className="h-10 w-10 text-dtf-blue" />
            </div>

            <h3 className="mb-2 text-2xl font-bold text-white">
              No how-to guides matched your search
            </h3>

            <p className="mx-auto max-w-2xl text-dtf-text-muted leading-8">
              Try a broader keyword like press, powder, artwork, cure, white ink,
              or maintenance.
            </p>
          </div>
        </BluePanel>
      )}
    </div>
  );
}